import React, { useState, useCallback, useEffect } from 'react';
import { BlockDto, postUpdateProjectScheduleCustomer } from '../siteApi';
import { Button } from '@/components/ui/button';
import { ScheduleGrid } from './schedule-grid/ScheduleGrid';
import { ScheduleSummary } from './ScheduleSummary';

const TOTAL_BLOCKS = 96; // 24 hours * 4 (15-minute blocks)
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface ScheduleProps {
  scheduleBlocks: BlockDto[];
  onScheduleChange?: (blocks: BlockDto[]) => void;
  customerId: string;
  projectId: string;
  onSave?: (blocks: BlockDto[]) => Promise<void>;
}

export const Schedule: React.FC<ScheduleProps> = ({
  scheduleBlocks,
  onScheduleChange,
  customerId,
  projectId,
  onSave,
}) => {
  const initialBlocks = (scheduleBlocks ?? []).filter((b) => !b.active);
  const [localBlocks, setLocalBlocks] = useState<BlockDto[]>(initialBlocks);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);  
  useEffect(() => {
    const sanitized = (scheduleBlocks ?? []).filter((b) => !b.active);
    setLocalBlocks(sanitized);
    setHasUnsavedChanges(false);
  }, [scheduleBlocks]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      if (onSave) {
        await onSave(localBlocks);
      } else {
        await postUpdateProjectScheduleCustomer(customerId, projectId, {
          blocks: localBlocks,
          blockSize: 15,
          projectId: projectId,
        });
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save schedule:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Convert BlockDto[] to boolean[][] for grid assignment.
  // `true` means armed, `false` means disarmed.
  // The API provides a list of disarmed blocks.
  const buildBlockAssignments = useCallback((blocks: BlockDto[]): boolean[][] => {
    const assignments: boolean[][] = Array.from(
      { length: DAYS_OF_WEEK.length },
      () => Array.from({ length: TOTAL_BLOCKS }, () => true) // Default to armed
    );
    blocks.forEach((block) => {
      if (
        block.dayOfWeek >= 0 &&
        block.dayOfWeek < DAYS_OF_WEEK.length &&
        block.blockIdx >= 0 &&
        block.blockIdx < TOTAL_BLOCKS
      ) {
        // Blocks in the list are disarmed.
        assignments[block.dayOfWeek][block.blockIdx] = block.active;
      }
    });
    return assignments;
  }, []);

  // Handle block change from ScheduleGrid (toggle armed/disarmed)
  const handleBlockChange = useCallback(
    (day: number, block: number, isArmed: boolean) => {
      
      setLocalBlocks((prevBlocks) => {
        const idx = prevBlocks.findIndex((b) => b.dayOfWeek === day && b.blockIdx === block);
        let updatedBlocks = prevBlocks;

        if (isArmed && idx !== -1) {
          // It is armed, but it's in our disarmed list. Remove it.
          updatedBlocks = prevBlocks.filter((_, i) => i !== idx);
        } else if (!isArmed && idx === -1) {
          // It is disarmed, but not in our disarmed list. Add it.
          updatedBlocks = [...prevBlocks, { dayOfWeek: day, blockIdx: block, active: false }];
        }

        if (updatedBlocks !== prevBlocks) {
          setHasUnsavedChanges(true);
          if (onScheduleChange) {
            onScheduleChange(updatedBlocks);
          }
        }
        return updatedBlocks;
      });
    },
    [onScheduleChange]
  );

  // Custom block render for armed/disarmed state
  const blockRender = ({
    assignment,
    isSelected,
    isDragging,
  }: {
    assignment: boolean;
    isSelected: boolean;
    isDragging: boolean;
  }) => {
    const baseColor = assignment ? 'bg-green-600' : 'bg-gray-500';
    const highlight = isSelected && isDragging ? 'opacity-80' : 'opacity-100';
    return (
      <div
        title={assignment ? 'Armed' : 'Disarmed'}
        className={`${baseColor} ${highlight} h-full w-full transition-opacity`}
      />
    );
  };

  const blockAssignments = buildBlockAssignments(localBlocks);

  return (
    <div className=" border-t border-gray-800  p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Arming Schedule</h3>

        <div className="flex items-center gap-6 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded border bg-green-500" />
            <span>Armed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded border bg-gray-600" />
            <span>Disarmed</span>
          </div>
        </div>
      </div>        <p className="pb-2 text-gray-400">This controls when the site will arm and disarm. It will fire events when green, and not when gray. </p>


      <ScheduleGrid
        daysOfWeek={DAYS_OF_WEEK}
        totalBlocks={TOTAL_BLOCKS}
        blockAssignments={blockAssignments}
        onBlockChange={handleBlockChange}
        blockRender={blockRender}
        selectionEnabled={true}
      />

      {hasUnsavedChanges && (
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            variant="default"
            size="sm"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      )}
      <div className="mt-4">
        <ScheduleSummary blocks={localBlocks} />
      </div>
    </div>
  );
};
